import { z } from 'zod';
import { Validation } from '@mews/framework';
import { RefundMode } from '@mews-commander/data-access';
import { MewsTerminalFormField } from '../ProcessPayment/MewsTerminalPayment/schema';
import { PaymentRefundFormField, PaymentRefundWithFXFormField } from './types';
import { getCustomMinErrorParams } from './utils';
import { PAYMENT_REFUND_MIN_AMOUNT } from './constants';

export const createPaymentRefundFormSchema = (refundableValue: number) =>
    z.discriminatedUnion(PaymentRefundFormField.RefundMode, [
        createBasePaymentRefundFormSchema(refundableValue).extend({
            [PaymentRefundFormField.RefundMode]: z.literal(RefundMode.Online),
        }),
        createBasePaymentRefundFormSchema(refundableValue).extend({
            [PaymentRefundFormField.RefundMode]: z.literal(RefundMode.InPerson),
            [MewsTerminalFormField.PaymentTerminalId]: z.string().uuid(),
        }),
    ]);

const createBasePaymentRefundFormSchema = (refundableValue: number) =>
    z.object({
        [PaymentRefundFormField.Amount]: z
            .number()
            .max(refundableValue)
            .refine((value) => Number(value) > PAYMENT_REFUND_MIN_AMOUNT, {
                params: getCustomMinErrorParams(PAYMENT_REFUND_MIN_AMOUNT),
            }),
        [PaymentRefundFormField.Notes]: Validation.String.Required,
    });

export const createPaymentRefundWithFXFormSchema = (refundableValue: number, propertyCurrencyAmountValue: number) =>
    z.discriminatedUnion(PaymentRefundFormField.RefundMode, [
        createBasePaymentRefundWithFXFormSchema(refundableValue, propertyCurrencyAmountValue).extend({
            [PaymentRefundFormField.RefundMode]: z.literal(RefundMode.Online),
        }),
        createBasePaymentRefundWithFXFormSchema(refundableValue, propertyCurrencyAmountValue).extend({
            [PaymentRefundFormField.RefundMode]: z.literal(RefundMode.InPerson),
            [MewsTerminalFormField.PaymentTerminalId]: z.string().uuid(),
        }),
    ]);

const createBasePaymentRefundWithFXFormSchema = (refundableValue: number, propertyCurrencyAmountValue: number) =>
    z.object({
        [PaymentRefundWithFXFormField.ChargedCurrencyAmount]: z
            .number()
            .max(refundableValue)
            .refine((value) => Number(value) > PAYMENT_REFUND_MIN_AMOUNT, {
                params: getCustomMinErrorParams(PAYMENT_REFUND_MIN_AMOUNT),
            }),
        [PaymentRefundWithFXFormField.PropertyCurrencyAmount]: z
            .number()
            .max(propertyCurrencyAmountValue)
            .refine((value) => Number(value) > PAYMENT_REFUND_MIN_AMOUNT, {
                params: getCustomMinErrorParams(PAYMENT_REFUND_MIN_AMOUNT),
            }),
        [PaymentRefundFormField.Notes]: Validation.String.Required,
    });

export type PaymentRefundFormValues = z.infer<ReturnType<typeof createPaymentRefundFormSchema>>;
export type PaymentRefundWithFXFormValues = z.infer<ReturnType<typeof createPaymentRefundWithFXFormSchema>>;
